import React from 'react';
import { CreditCard, Gift, Wallet, LayoutDashboard, MessageCircle, Home, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useScrollAnimation, useStaggerAnimation } from '../hooks/useScrollAnimation';

const FeaturesGrid = () => {
  const { t, isRTL } = useLanguage();
  const [headerRef, headerVisible] = useScrollAnimation();
  const [cardsRef, visibleCards] = useStaggerAnimation(6, 100);

  const features = [
    { 
      key: 'cardIntel',
      icon: CreditCard, 
      gradient: 'from-[#0071E3] to-[#5856D6]',
      data: t.features.cardIntel
    },
    { 
      key: 'rewards',
      icon: Gift, 
      gradient: 'from-[#FF9500] to-[#FFCC00]',
      data: t.features.rewards
    },
    { 
      key: 'smartPay',
      icon: Wallet, 
      gradient: 'from-[#34C759] to-[#30D158]',
      data: t.features.smartPay
    },
    { 
      key: 'dashboard',
      icon: LayoutDashboard, 
      gradient: 'from-[#5856D6] to-[#AF52DE]',
      data: t.features.dashboard
    },
    { 
      key: 'assistant',
      icon: MessageCircle, 
      gradient: 'from-[#FF2D55] to-[#FF6B6B]',
      data: t.features.assistant
    },
    { 
      key: 'mortgage',
      icon: Home, 
      gradient: 'from-[#007AFF] to-[#5AC8FA]',
      data: t.features.mortgage
    }
  ];

  return (
    <section id="features" className="py-20 md:py-24 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-14 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-[#0071E3]/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-1.5 h-1.5 bg-[#0071E3] rounded-full"></div>
            <span className="text-[11px] font-semibold text-[#0071E3] uppercase tracking-wide font-inter">
              {t.features.badge}
            </span>
          </div>
          <h2 className="font-dm text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight text-[#1D1D1F] mb-4 leading-tight">
            {t.features.title}
          </h2>
          <p className="text-lg text-[#6e6e73] font-inter max-w-2xl mx-auto">
            {t.features.subtitle}
          </p>
        </div>

        {/* Features Bento Grid */}
        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, index) => (
            <div 
              key={feature.key}
              className={`group bg-white rounded-2xl p-6 border border-[#E5E5E5]/60 hover:border-[#0071E3]/30 transition-all duration-500 hover-lift card-shine ${
                index === 0 || index === 3 ? 'lg:col-span-2' : ''
              } ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${isRTL ? 'text-right' : ''}`}
            >
              <div className={`flex flex-col h-full ${index === 0 || index === 3 ? 'md:flex-row md:gap-8' : ''}`}>
                {/* Icon & Title */}
                <div className={`${index === 0 || index === 3 ? 'md:w-1/3' : ''}`}>
                  <div 
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-gradient-to-br ${feature.gradient} group-hover:scale-110 group-hover:shadow-lg transition-all duration-300`}
                  >
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-dm text-xl font-semibold text-[#1D1D1F] mb-3 group-hover:text-[#0071E3] transition-colors">
                    {feature.data.title}
                  </h3>
                </div>
                
                {/* Content */}
                <div className={`flex-1 ${index === 0 || index === 3 ? 'md:w-2/3' : ''}`}>
                  <p className="text-[#6e6e73] font-inter text-[15px] leading-relaxed mb-5">
                    {feature.data.desc}
                  </p>
                  
                  {/* Bullet Points */}
                  <div className={`grid ${index === 0 || index === 3 ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                    {feature.data.bullets.map((bullet, i) => (
                      <div 
                        key={i} 
                        className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}
                      >
                        <CheckCircle className="w-4 h-4 text-[#34C759] flex-shrink-0" />
                        <span className="text-[13px] text-[#1D1D1F] font-inter">{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
